import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Users, MapPin, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format, differenceInDays, parseISO } from 'date-fns';

const RoomDetails = () => {
  const { roomId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { checkIn, checkOut } = location.state || {};

  const [room, setRoom] = useState(null);
  const [totalPrice, setTotalPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);
  const [guestInfo, setGuestInfo] = useState({
    guest_name: '',
    guest_email: '',
    guest_phone: '',
    num_guests: 1
  });

  useEffect(() => {
    if (!checkIn || !checkOut) {
      toast.error('Please select dates from the home page');
      navigate('/');
      return;
    }
    fetchRoomDetails();
    calculatePrice();
  }, []);

  const fetchRoomDetails = async () => {
    try {
      const response = await axios.post(`${API}/rooms/available`, {
        check_in: checkIn,
        check_out: checkOut
      });
      const foundRoom = response.data.find(r => r.id === roomId);
      if (!foundRoom) {
        toast.error('Room not available');
        navigate('/');
        return;
      }
      setRoom(foundRoom);
    } catch (error) {
      toast.error('Failed to load room details');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = async () => {
    try {
      const response = await axios.post(`${API}/rooms/price`, {
        room_id: roomId,
        check_in: checkIn,
        check_out: checkOut
      });
      setTotalPrice(response.data.total_price);
    } catch (error) {
      toast.error('Failed to calculate price');
    }
  };

  const handleBooking = async () => {
    if (!guestInfo.guest_name || !guestInfo.guest_email || !guestInfo.guest_phone) {
      toast.error('Please fill in all guest information');
      return;
    }

    setBooking(true);
    try {
      // Create booking
      const bookingResponse = await axios.post(`${API}/bookings`, {
        room_id: roomId,
        ...guestInfo,
        check_in: checkIn,
        check_out: checkOut
      });

      const bookingId = bookingResponse.data.id;

      // Create payment intent
      const paymentResponse = await axios.post(`${API}/bookings/payment-intent`, {
        booking_id: bookingId,
        origin_url: window.location.origin
      });

      // Redirect to Stripe
      window.location.href = paymentResponse.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create booking');
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!room) return null;

  const nights = differenceInDays(parseISO(checkOut), parseISO(checkIn));

  return (
    <div className="h-screen w-screen flex flex-col bg-gradient-to-br from-blue-50 via-white to-indigo-50 overflow-hidden">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-md z-50 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center">
          <Button variant="ghost" onClick={() => navigate('/')} className="mr-4" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <MapPin className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Kaiser Bridge Hotel
            </h1>
          </div>
        </div>
      </header>

      {/* Main Content - Scrollable */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Room Details */}
          <div>
            <div className="aspect-video bg-gradient-to-br from-blue-100 to-indigo-100 rounded-xl overflow-hidden mb-6">
              {room.image_url ? (
                <img src={room.image_url} alt={room.name} className="w-full h-full object-cover" />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <MapPin className="w-24 h-24 text-blue-400" />
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl p-6 shadow-lg">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-3xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{room.name}</h2>
                <span className="text-sm font-semibold bg-blue-100 text-blue-700 px-3 py-1 rounded-full">Room {room.room_number}</span>
              </div>

              <p className="text-gray-600 mb-6">{room.description}</p>

              <div className="space-y-4">
                <div className="flex items-center text-gray-700">
                  <Users className="w-5 h-5 mr-2 text-blue-600" />
                  <span>Maximum {room.max_guests} guests</span>
                </div>

                {room.amenities && room.amenities.length > 0 && (
                  <div>
                    <h3 className="font-semibold mb-2 text-gray-800">Amenities</h3>
                    <div className="flex flex-wrap gap-2">
                      {room.amenities.map((amenity, idx) => (
                        <span key={idx} className="bg-gray-100 px-3 py-1 rounded-full text-sm text-gray-700">
                          {amenity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t pt-4 mt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Base Rate</span>
                    <span className="text-xl font-bold text-blue-600">${room.base_price}/night</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Form */}
          <div>
            <Card className="shadow-xl border-0 sticky top-24" data-testid="booking-form">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Complete Your Booking</h3>

                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Check-in</span>
                    <span className="font-semibold">{format(parseISO(checkIn), 'MMM dd, yyyy')}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Check-out</span>
                    <span className="font-semibold">{format(parseISO(checkOut), 'MMM dd, yyyy')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{nights} night{nights > 1 ? 's' : ''}</span>
                    <span className="text-2xl font-bold text-blue-600">${totalPrice}</span>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <Label htmlFor="guest_name">Full Name *</Label>
                    <Input
                      id="guest_name"
                      placeholder="John Doe"
                      value={guestInfo.guest_name}
                      onChange={(e) => setGuestInfo({ ...guestInfo, guest_name: e.target.value })}
                      data-testid="guest-name-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="guest_email">Email *</Label>
                    <Input
                      id="guest_email"
                      type="email"
                      placeholder="john@example.com"
                      value={guestInfo.guest_email}
                      onChange={(e) => setGuestInfo({ ...guestInfo, guest_email: e.target.value })}
                      data-testid="guest-email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="guest_phone">Phone Number *</Label>
                    <Input
                      id="guest_phone"
                      placeholder="+1 234 567 8900"
                      value={guestInfo.guest_phone}
                      onChange={(e) => setGuestInfo({ ...guestInfo, guest_phone: e.target.value })}
                      data-testid="guest-phone-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="num_guests">Number of Guests *</Label>
                    <Input
                      id="num_guests"
                      type="number"
                      min="1"
                      max={room.max_guests}
                      value={guestInfo.num_guests}
                      onChange={(e) => setGuestInfo({ ...guestInfo, num_guests: parseInt(e.target.value) })}
                      data-testid="num-guests-input"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleBooking}
                  className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold text-lg"
                  disabled={booking}
                  data-testid="proceed-payment-btn"
                >
                  {booking ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    `Book Now - $${totalPrice}`
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
        </div>
      </main>
    </div>
  );
};

export default RoomDetails;